<template>
  <div class="news">
    <LayoutSearchHeader
      :title="title"
      :icon="config.icons.news.icon"
      :paginator="paginator"
      :accessControls="accessControls"
      :controls="controls"
      :noControls="false"
      :loading="loading"
      :noResults="noResults"
      :timeout="timeout"
      @controlsChanged="controlsChanged"
    />

    <template v-if="!loading && (noResults || paginator.total === 0 || timeout)">
      <div class="news__error">
        <div class="news__error--icon">
          <svg v-if="noResults || paginator.total === 0" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm1 18h-2v-8h2v8zm-1-12.25c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25-1.25-.56-1.25-1.25.56-1.25 1.25-1.25z"/></svg>
          <svg v-else-if="timeout" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M16.142 2l5.858 5.858v8.284l-5.858 5.858h-8.284l-5.858-5.858v-8.284l5.858-5.858h8.284zm.829-2h-9.942l-7.029 7.029v9.941l7.029 7.03h9.941l7.03-7.029v-9.942l-7.029-7.029zm-5.971 6h2v8h-2v-8zm1 12.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/></svg>
        </div>
        <div class="news__error--text">
          <p v-if="noResults || paginator.total === 0">No results!<br><span>We could not find any matching news...</span></p>
          <p v-else-if="timeout">Request was timed out!<br><span>Please try again later...</span></p>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="news__content">
        <UtilityList
          type="news"
          :results="results"
          :fields="listFields"
          :showScroll="false"
          :loading="loading"
          @clicked="itemClicked"
        />
      </div>

      <div
        v-if="$route.name === 'News'"
        class="news__footer"
      >
        <UtilityPaginator
          class="news__paginator"
          layout="full"
          position="bottom"
          :showPages="false"
          :currentPage="paginator.current_page"
          :lastPage="paginator.last_page"
          :perPage="paginator.per_page"
          :from="paginator.from"
          :to="paginator.to"
          :total="paginator.total"
          :loading="loading"
          type="news"
          @changed="paginatorChanged"
        />
      </div>
    </template>
  </div>
</template>

<script>
import LayoutSearchHeader from '@/components/layout/SearchHeader.vue'
import UtilityList from '@/components/utility/List.vue'
import UtilityPaginator from '@/components/utility/Paginator.vue'
import configFile from '@/helpers/config.js'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    LayoutSearchHeader,
    UtilityList,
    UtilityPaginator
  },
  props: {
    title: {
      type: String,
      default: 'News'
    },
    sliderName: {
      type: String,
      default: 'slider'
    },
    display: {
      type: String,
      default: 'default'
    },
    selectedResultId: {
      type: [Boolean, String],
      default: false
    },
    results: {
      type: Object
    },
    paginator: {
      type: Object
    },
    loading: {
      type: Boolean,
      default: true
    },
    timeout: {
      type: Boolean,
      default: false
    },
    noResults: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      config: configFile,
      listFields: [{
        key: 'title',
        name: 'Title',
      },{
        key: 'url',
        name: 'URL'
      },{
        key: 'publisher',
        name: 'Publisher'
      },{
        key: 'posted_on',
        name: 'Published'
      }],
      accessControls: {
        'maximized': [{
          'group': [{
            'name': 'news.access',
            'value': 'all',
            'label': 'Show <u>all</u> results',
            'icon': '<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M8 9v-3c0-2.206 1.794-4 4-4s4 1.794 4 4v3h2v-3c0-3.313-2.687-6-6-6s-6 2.687-6 6v3h2zm.746 2h2.831l-8.577 8.787v-2.9l5.746-5.887zm12.254 1.562v-1.562h-1.37l-12.69 13h2.894l11.166-11.438zm-6.844-1.562l-11.156 11.431v1.569h1.361l12.689-13h-2.894zm6.844 7.13v-2.927l-8.586 8.797h2.858l5.728-5.87zm-3.149 5.87h3.149v-3.226l-3.149 3.226zm-11.685-13h-3.166v3.244l3.166-3.244z"/></svg>',
            'iconSize': 'lg',
            'color': 'blue',
            'defaultColor': 'blue'
          },{
            'name': 'news.access',
            'value': 'unlocked',
            'label': 'Show <u>available</u> results',
            'icon': '<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M8 10v-4c0-2.206 1.795-4 4-4s4 1.794 4 4v1h2v-1c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-13zm11 12h-14v-10h14v10z"/></svg>',
            'iconSize': 'md',
            'color': 'green',
            'defaultColor': 'green'
          },{
            'name': 'news.access',
            'value': 'locked',
            'label': 'Show <u>unavailable</u> results',
            'icon': '<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M21.658 3.786l-3.658 3.318v-1.104c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v10.707l-2 1.813 1.346 1.48 20.654-18.734-1.342-1.48zm-16.658 15.107v-6.893h7.625l-7.625 6.893zm11-9.975l-1.194 1.082h-6.806v-4c0-2.205 1.795-4 4-4s4 1.795 4 4v2.918zm5 1.082v14h-16.391l2.204-2h12.187v-10h-1.176l2.191-2h.985z"/></svg>',
            'iconSize': 'md',
            'color': 'red',
            'defaultColor': 'red'
          }]
        }]
      },
      controls: {
        'minimized': [{
          'group': [{
            'name': 'search.view',
            'value': 'news',
            'label': 'View all news',
            'text': 'View all',
            'icon': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M21 13v10h-21v-19h12v2h-10v15h17v-8h2zm3-12h-10.988l4.035 4-6.977 7.07 2.828 2.828 6.977-7.07 4.125 4.172v-11z"/></svg>',
            'iconSize': 'md',
            'flipLayout': true,
            'color': 'blue'
          }]
        }],
        'maximized': [{
          'group': [{
            'name': 'news.sort',
            'value': ['-published', 'published'],
            'label': ['Sort from <u>newest</u> to oldest', 'Sort from <u>oldest</u> to newest'],
            'icon': ['<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M6 22l6-8h-4v-12h-4v12h-4l6 8zm13.936-6.323c.929-.998 3.024-1.006 3.737.691.212.503.319 1.217.327 1.944.008.774-.098 1.563-.313 2.129-.398 1.04-1.125 1.559-2.186 1.559-1.005 0-1.938-.605-2.056-1.758h1.32c.062.392.339.67.795.67.904 0 1.06-1.002 1.104-1.893-.63.79-1.915.743-2.73.016-.869-.774-.807-2.488.002-3.358zm2.23 2.677c.599-.383.547-1.452.184-1.907-.336-.42-.963-.421-1.314-.144-.512.399-.466 1.505-.137 1.909.269.331.866.404 1.267.142zm-4.166-12.879v-1.498c.565-.025 3.163.046 3.163-1.977h1.837v11h-2.255v-7.525h-2.745z"/></svg>', '<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M6 22l6-8h-4v-12h-4v12h-4l6 8zm12.43-4.814v-.934c.352-.015 1.901.029 1.901-1.232h1.146v6.98h-1.407v-4.814h-1.64zm-.927-14.097c.646-.694 1.647-1.089 2.666-1.089 1.319 0 2.667.663 3.311 2.194.346.824.52 1.907.52 3.251 0 3.048-.947 5.757-3.994 5.757-1.606 0-3.099-.968-3.288-2.812h2.112c.099.628.542 1.071 1.272 1.071 1.445 0 1.693-1.601 1.763-3.027-1.033 1.299-3.106 1.15-4.366.026-1.388-1.238-1.291-3.98.004-5.371zm3.566 4.282c.481-.308.722-.841.722-1.599 0-.61-.143-1.093-.428-1.451-.285-.357-.676-.536-1.172-.536-.362 0-.671.102-.93.305-.406.317-.609.855-.609 1.614 0 .64.13 1.119.391 1.439.26.32.66.48 1.201.48.293.001.568-.084.825-.252z"/></svg>'],
            'iconSize': 'lg',
            'color': 'blue'
          },{
            'name': 'news.sort',
            'value': ['relevance', '-relevance'],
            'label': ['Sort from <u>most</u> to least relevant', 'Sort from <u>least</u> to most relevant'],
            'icon': ['<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M6 21l6-8h-4v-10h-4v10h-4l6 8zm16-12h-8v-2h8v2zm2-6h-10v2h10v-2zm-4 8h-6v2h6v-2zm-2 4h-4v2h4v-2zm-2 4h-2v2h2v-2z"/></svg>', '<svg xmlns="http://www.w3.org/2000/svg" height="100%" width="100%" viewBox="0 0 24 24"><path d="M6 21l6-8h-4v-10h-4v10h-4l6 8zm16-4h-8v-2h8v2zm2 2h-10v2h10v-2zm-4-8h-6v2h6v-2zm-2-4h-4v2h4v-2zm-2-4h-2v2h2v-2z"/></svg>'],
            'iconSize': 'lg',
            'color': 'blue'
          }]
        }]
      }
    }
  },
  computed: {
    settings () {
      return this.$store.getters['app/getSettings']
    }
  },
  methods: {
    itemClicked (value) {
      this.$emit('itemClicked', value)
    },
    controlsChanged (setting, value) {
      this.$emit('controlsChanged', setting, value)
    },
    paginatorChanged (value) {
      this.$emit('paginatorChanged', value)
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.news {
  background-color: $black;

  &__header {
    @include font('content--md', $white, 0.7);
    background-color: $bg-2;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    width: 100%;
    height: 74px;
    user-select: none;

    @media screen and (min-width: $breakpointLg) {
      @include font('content--lg', $white, 0.7);
      height: 82px;
    }

    &--small {
      display: flex;
      justify-content: space-between;
      height: 36px;

      @media screen and (min-width: $breakpointLg) {
        @include font('content--lg', $white, 0.7);
        height: 40px;
      }
    }

    &--top {
      width: 100%;
      height: 36px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid $black;
      overflow: hidden;

      @media screen and (min-width: $breakpointLg) {
        height: 40px;
      }
    }

    &--bottom {
      //background-color: $bg-4;
      width: 100%;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-top: 1px solid rgba($white, 0.1);

      @media screen and (min-width: $breakpointLg) {
        height: 40px;
      }
    }

    &--left {
      display: flex;
      align-items: center;
      flex: 1;
    }

    &--right {
      flex: 1;
      text-align: right;
      margin-right: 12px;
    }
  }

  &__content {
    border-bottom: 1px solid $black;
  }

  &__footer {
    background-color: $bg-2;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    height: 35px;

    @media screen and (min-width: $breakpointLg) {
      height: 39px;
    }
  }

  &__paginator {
    position: relative;
    top: -1px;
  }

  &__icon {
    width: 18px;
    min-width: 18px;
    max-width: 18px;
    height: 18px;
    margin: 0 0 0 16px;
    fill: $blue;
  }

  &__info {
    display: flex;
    align-items: center;
    overflow: hidden;

    &--xs {
      display: block;

      @media screen and (min-width: $breakpointSm) {
        display: none;
      }
    }

    &--sm {
      display: none;

      @media screen and (min-width: $breakpointSm) {
        display: block;
      }

      @media screen and (min-width: $breakpointMd) {
        display: none;
      }
    }

    &--md {
      display: none;

      @media screen and (min-width: $breakpointMd) {
        display: block;
      }
    }

    &--title {
      text-transform: uppercase;
      margin-right: 8px;
      color: rgba($white, 0.9) !important;
    }

    p {
      @include font('content--sm', $white, 0.7);
      margin: 0 12px;

      @media screen and (min-width: $breakpointLg) {
        @include font('content--md', $white, 0.7);
        margin: 0 12px;
      }

      span {
        color: $white;
      }
    }
  }

  &__error {
    @include font('content--md', $white);
    display: flex;
    align-items: center;
    background-color: $bg-4;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    width: calc(100% - 32px);
    padding: 8px 16px;
    line-height: 1.5rem;

    @media screen and (min-width: $breakpointLg) {
      @include font('content--lg', $white);
      padding: 8px 16px;
      line-height: 1.65rem;
    }

    &--icon {
      width: 48px;
      min-width: 48px;
      max-width: 48px;
      height: 48px;
      margin-right: 24px;
      fill: rgba($white, 0.25);
    }

    &--text {
      text-transform: uppercase;

      span {
        color: rgba($white, 0.7);
        text-transform: none;
      }
    }
  }
}

</style>
